import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { selectEssDataConsumption } from '../../../store/slices/essDataConsumptionSlice';
import { selectEssSwitch } from '../../../store/slices/essSwitchSlice';
import { selectHpDataConsumption } from '../../../store/slices/hpDataConsumptionSlice';
import { selectHpElectricSwitch } from '../../../store/slices/hpElectricSwitchSlice';
import { selectHpGasSwitch } from '../../../store/slices/hpGasSwitchSlice';
import { selectLocations } from '../../../store/slices/locationsSlice';
import { selectTelemetry } from '../../../store/slices/telemetrySlice';
import { selectTesDataConsumption } from '../../../store/slices/tesDataConsumptionSlice';
import { selectTesSwitch } from '../../../store/slices/tesSwitchSlice';
import { selectTgsDataConsumption } from '../../../store/slices/tgsDataConsumptionSlice';
import { selectTgsSwitch } from '../../../store/slices/tgsSwitchSlice';
import {
  alignItemsFlexStart,
  flexBoxCenter,
  layerA90Deg,
  layerBDark,
} from '../../../styles/commonStyles';
import SearchBar from './SearchBar';
import SwitchSpecification from './SwitchSpecification';
import Title from './Title';
import testData from '../../../../test_data/testData';
import { filteredSuggestionsHandler } from '../../../../helpers/helpers';
import { selectUserPermissions } from '../../../store/slices/userSlice';
import { PERMISSIONS } from '../../../../constants';
import { logger } from '../../../../helpers/logger';

const initialState = {
  selectedEssMachines: [],
  selectedTgsMachines: [],
  selectedTesMachines: [],
  selectedEssDcMachines: [],
  selectedTgsDcMachines: [],
  selectedTesDcMachines: [],
  selectedHpDcMachines: [],
  selectedHpElectricMachines: [],
  selectedHpGasMachines: [],
};

const ACTIONS = {
  SETESSMACHINES: 'setEssMachines',
  SETTGSMACHINES: 'setTgsMachines',
  SETTESMACHINES: 'setTesMachines',
  SETESSDCMACHINES: 'setEssDcMachines',
  SETTGSDCMACHINES: 'setTgsDcMachines',
  SETTESDCMACHINES: 'setTesDcMachines',
  SETHPDCMACHINES: 'setHpDcMachines',
  SETHPELECTRICMACHINES: 'setHpElectricMachines',
  SETHPGASMACHINES: 'setHpGasMachines',
};

const MainSwitchesSpecifications = ({
  telemetryData,
  scheduleData,
  selectedSystem,
  controlSelectionDisplay,
  isDc,
  selectSpecific
}) => {
 
  const closeArrow = './images/white-triangle-pointing-right.svg';
  const openArrow = './images/white-triangle-pointing-down.svg';
  const essDropPinLogo = './images/ess-droppin-logo-for-nivo-chart.svg';
  const tgsDropPinLogo = './images/tgs-droppin-logo-for-nivo-chart.svg';
  const tesDropPinLogo = './images/tes-droppin-logo-for-nivo-chart.svg';
  const tgsTesDropPinLogo = './images/tgs-tes-droppin-logo-for-nivo-chart.svg';

  const telemetryState = useSelector(selectTelemetry);
  const isSearch = telemetryState.isSearch;

  const { flatEssSwitch } = useSelector(selectEssSwitch);
  const { flatTgsSwitch } = useSelector(selectTgsSwitch);
  const { flatTesSwitch } = useSelector(selectTesSwitch);
  const locations = useSelector(selectLocations);
  const hpElectricSwitch = useSelector(selectHpElectricSwitch);
  const hpGasSwitch = useSelector(selectHpGasSwitch);
  const { essDataConsumptionSwitch,essDataConsumptionSwitchSpecificLocation } = useSelector(selectEssDataConsumption);
  const { tesDataConsumptionSwitch,tesDataConsumptionSwitchSpecificLocation } = useSelector(selectTesDataConsumption);
  const { tgsDataConsumptionSwitch } = useSelector(selectTgsDataConsumption);
  const hpDataConsumptionSwitch = useSelector(selectHpDataConsumption);

  const [switchIdx, setSwitchIdx] = useState(null);
  const [isExpand, setIsExpand] = useState([]);
  const [inputSearch, setInputSearch] = useState(['']);
  const [expandButtonTitle, setExpandButtonTitle] = useState('expand');
  const [system, setSystem] = useState(null);
  const [src, setSrc] = useState([]);
  const [systemSwitches, setSystemSwitches] = useState();
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [specificLocationsOpeningControl, setSpecificLocationsOpeningControl] =
    useState([]);
  const [totalDataConsumpOrEnergyConsump, setTotalDataConsumpOrEnergyConsump] =
    useState([]);
  const [totalUsageHours, setTotalUsageHours] = useState([]);
  const [totalNumOfSwt, setTotalNumOfSwt] = useState([]);
  const [isDataChanged, setIsDataChanged] = useState('');
  const [searchedSwtName, setSearchedSwtName] = useState('');
  const permissions = useSelector(selectUserPermissions);

  const displayStart =
    scheduleData.start.date !== null
      ? `${scheduleData.start.time.hour}:${scheduleData.start.time.minute} ${
          scheduleData.start.time.division
        }-${scheduleData.start.date.getDate()}/${
          scheduleData.start.date.getMonth() + 1
        }/${scheduleData.start.date.getFullYear()}`
      : ' -----';

  const displayEnd =
    scheduleData.end.date !== null
      ? `${scheduleData.end.time.hour}:${scheduleData.end.time.minute} ${
          scheduleData.end.time.division
        }-${scheduleData.end.date.getDate()}/${
          scheduleData.end.date.getMonth() + 1
        }/${scheduleData.end.date.getFullYear()}`
      : ' -----';

  const checkIsSystemAndSwitchesSelected =
    systemSwitches !== undefined && isSearch && controlSelectionDisplay;

  const [inputSwitchName, setInputSwitchName] = useState('');

  const initialDisplaySystemState = [
    [
      'ess - electric switch systems',
      '/images/ess-map-icon.svg',
      15,
      false,
      Object.keys(flatEssSwitch),
      'ess',
    ],
    [
      'tgs - typhoon gas systems',
      '/images/tgs-map-icon.svg',
      10,
      false,
      Object.keys(flatTgsSwitch),
      'tgs',
    ],
    [
      'tes - typhoon electric systems',
      '/images/tes-map-icon.svg',
      11,
      false,
      Object.keys(flatTesSwitch),
      'tes',
    ],
    [
      'ess - dc',
      '/images/ess-map-icon.svg',
      15,
      false,
      Object.keys(essDataConsumptionSwitch),
      'essDc',
    ],
    [
      'tgs/tes - dc',
      '/images/tgs-map-icon.svg',
      10,
      false,
      Object.keys(tgsDataConsumptionSwitch),
      'tgsDc',
    ],
    [
      'tes - typhoon electric systems',
      '/images/tes-map-icon.svg',
      15,
      false,
      Object.keys(tesDataConsumptionSwitch),
      'tesDc',
    ],
    [
      'hp - switch systems',
      '/images/tes-map-icon.svg',
      15,
      false,
      Object.keys(hpDataConsumptionSwitch),
      'hpDc',
    ],
    [
      'hp - electric switch systems',
      '/images/ess-map-icon.svg',
      7,
      false,
      Object.keys(hpElectricSwitch),
      'hpEc',
    ],
    [
      'hp - gas switch systems',
      '/images/ess-map-icon.svg',
      7,
      false,
      Object.keys(hpGasSwitch),
      'hpGc',
    ],
  ];

  const createIsExpandAndSrcState = useCallback(
    (data) => {
      const copyData = [...data];
      const copyData2 = [...data];
      const arrIsExpand = copyData?.fill(false);
      const arrowImages = copyData2?.fill(closeArrow);
      setIsExpand(arrIsExpand);
      setSrc(arrowImages);
    },
    [selectedSystem]
  );

  const createLocationsArr = (data) => {
    const dataLocationsArr = data.map((value) => false);
    const specificLocationArr = Object.values(flatTgsSwitch).map((el) => {
      const numOfEl = Object.keys(el).length;
      if (numOfEl === 0 || Object.values(el)[0]?.machineType) {
        return [];
      } else {
        const emptyArr = new Array(numOfEl);
        const arr = emptyArr;
        return arr.fill(false);
      }
    });

    const dataEnergyHoursArr = data.map((value) => '--');
    const numOfSwt = data.map((value) => 0);
    setTotalDataConsumpOrEnergyConsump(dataEnergyHoursArr);
    setTotalUsageHours(dataEnergyHoursArr);
    setSelectedLocations(dataLocationsArr);
    setSpecificLocationsOpeningControl(specificLocationArr);

    setTotalNumOfSwt(numOfSwt);
    setIsDataChanged((prev) => `${prev}1`);
  };
  const setSystemInfoHandler = (switchData, id) => {
    const displayInfo = [...initialDisplaySystemState[id]];
    displayInfo[4] = Object.keys(switchData || {});

    setSystemSwitches([displayInfo]);
    setSwitchIdx(id);
    createIsExpandAndSrcState(displayInfo[4]);
    createLocationsArr(displayInfo[4]);
    setSystem(switchData);
  };

  useEffect(() => {
    switch (selectedSystem) {
      case 'ess - electric switch systems - ec':
        setSystemInfoHandler(flatEssSwitch, 0);
        break;
      case 'tgs - typhoon gas systems - gc':
        setSystemInfoHandler(flatTgsSwitch, 1);
        break;
      case 'tes - typhoon electric systems - ec':
        setSystemInfoHandler(flatTesSwitch, 2);
        break;
      case 'ess - dc':
        setSystemInfoHandler(flatEssSwitch, 3); 
        break;
      case 'tgs/tes - dc':
        setSystemInfoHandler(flatTesSwitch, 4);
        break;
      case 'hp - heating platform - dc':
        setSystemInfoHandler(hpDataConsumptionSwitch, 6);
        break;
      case 'hp - heating platform - ec':
        setSystemInfoHandler(hpElectricSwitch, 7);
        break;
      case 'hp - heating platform - gc':
        setSystemInfoHandler(hpGasSwitch, 8);
        break;
      default:
        setSystemSwitches([initialDisplaySystemState[0]]);
        break;
    }
  }, [selectedSystem, isSearch, telemetryData]);

  useEffect(() => {
    setInputSearch(['']);
  }, [telemetryData]);

  const filledCopy = (arr, value) => arr.map(() => value);

  const handleClearSearch = useCallback(() => {
    setInputSwitchName('');
    const copyInputSearch = [...inputSearch];
    copyInputSearch[0] = '';
    setInputSearch(copyInputSearch);
    setSearchedSwtName('');
    setIsExpand(filledCopy(isExpand, false));
    setSrc(filledCopy(src, closeArrow));
  }, [inputSearch, isExpand, src]);

  const handleExpandSearch = useCallback(() => {
    if (expandButtonTitle === 'close') {
      setExpandButtonTitle('expand');
      setIsExpand(filledCopy(isExpand, false));
      setSrc(filledCopy(src, closeArrow));
    } else {
      setExpandButtonTitle('close');
      setIsExpand(filledCopy(isExpand, true));
      setSrc(filledCopy(src, openArrow));
    }
  }, [expandButtonTitle, isExpand, src]);

  const handleExpandEachLocation = useCallback(
    (idx) => {
      const copyIsExpand = [...isExpand];
      const copySrc = [...src];
      if (isExpand[idx]) {
        copyIsExpand[idx] = false;
        setIsExpand(copyIsExpand);
        copySrc[idx] = closeArrow;
        setSrc(copySrc);
      } else {
        copyIsExpand[idx] = true;
        setIsExpand(copyIsExpand);
        copySrc[idx] = openArrow;
        setSrc(copySrc);
      }
      setSearchedSwtName('');
    },
    [isExpand, src]
  );

  const [selectedSuggestionIdx, setSelectedSuggestionIdx] = useState(0);

  const [displaySuggestions, setDisplaySuggestions] = useState(false);
  const [activatedIndex, setActivatedIndex] = useState(null);
  const [machineSuggestions, setMachineSuggestions] = useState(null);

  const reducer = (state, action) => {
    switch (action.type) {
      case 'setEssMachines':
        return { ...state, selectedEssMachines: action.payload };
      case 'setTgsMachines':
        return { ...state, selectedTgsMachines: action.payload };
      case 'setTesMachines':
        return { ...state, selectedTesMachines: action.payload };
      case 'setEssDcMachines':
        return { ...state, selectedEssDcMachines: action.payload };
      case 'setTgsDcMachines':
        return { ...state, selectedTgsDcMachines: action.payload };
      case 'setTesDcMachines':
        return { ...state, selectedTesDcMachines: action.payload };
      case 'setHpDcMachines':
        return { ...state, selectedHpDcMachines: action.payload };
      case 'setHpElectricMachines':
        return { ...state, selectedHpElectricMachines: action.payload };
      case 'setHpGasMachines':
        return { ...state, selectedHpGasMachines: action.payload };
      default:
        throw new Error();
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const isDevice = (value) =>
    value && typeof value === 'object' && 'isSelected' in value;

  const collectSelectedMachines = (switchSystemData) =>
    Object.entries(switchSystemData || {}).flatMap(([location, zone]) =>
      Object.entries(zone || {}).flatMap(([key, value]) => {
        if (isDevice(value)) {
          return value.isSelected ? [`${location} - ${key}`] : [];
        }

        return Object.entries(value || {})
          .filter(([, machine]) => machine?.isSelected)
          .map(([machine]) => `${location} - ${key} - ${machine}`);
      })
    );

  const dispatchHandler = (switchSystemData, actionType) => {
    dispatch({
      type: actionType,
      payload: collectSelectedMachines(switchSystemData),
    });
  };
  useEffect(() => {
    if (isSearch) {
      switch (selectedSystem) {
        case 'ess - electric switch systems - ec':
          dispatchHandler(flatEssSwitch, ACTIONS.SETESSMACHINES);
          break;
        case 'tgs - typhoon gas systems - gc':
          dispatchHandler(flatTgsSwitch, ACTIONS.SETTGSMACHINES);
          break;
        case 'tes - typhoon electric systems - ec':
          dispatchHandler(flatTesSwitch, ACTIONS.SETTESMACHINES);
          break;
        case 'ess - dc':
          dispatchHandler(flatEssSwitch, ACTIONS.SETESSDCMACHINES);
          break;
        case 'tgs/tes - dc':
          dispatchHandler(flatTesSwitch, ACTIONS.SETTGSDCMACHINES);
          break;
        case 'hp - heating platform - dc':
          dispatchHandler(hpDataConsumptionSwitch, ACTIONS.SETHPDCMACHINES);
          break;
        case 'hp - heating platform - ec':
          dispatchHandler(hpElectricSwitch, ACTIONS.SETHPELECTRICMACHINES);
          break;
        case 'hp - heating platform - gc':
          dispatchHandler(hpGasSwitch, ACTIONS.SETHPGASMACHINES);
          break;
        default:
          break;
      }
    }
  }, [isSearch, telemetryData]);

  const filteredSuggestions = filteredSuggestionsHandler(
    machineSuggestions,
    locations,
    inputSwitchName
  );

  useEffect(() => {
    filteredSuggestions?.length >= 1 && inputSwitchName?.length >= 2
      ? setDisplaySuggestions(true)
      : setDisplaySuggestions(false);
  }, [filteredSuggestions, inputSwitchName]);

  useEffect(() => {
    const zones = Object.keys(system || {});
    const selectedDevices = zones.reduce(
      (total, zone) =>
        total +
        Object.values(system[zone] || {}).filter((d) => d?.isSelected).length,
      0
    );

    logger.debug('[TELEMETRY SEARCH]', {
      selectedSystem,
      switchIdx,
      activatedIndex,
      isSearch,
      zonesInSystem: zones.length,
      selectedDevicesInSystem: selectedDevices,
      machineSuggestions: machineSuggestions?.length ?? null,
      sampleSuggestion: machineSuggestions?.[0] ?? null,
      inputSwitchName,
      filteredSuggestions: filteredSuggestions?.length ?? null,
      displaySuggestions,
    });
  }, [
    system,
    machineSuggestions,
    inputSwitchName,
    filteredSuggestions,
    displaySuggestions,
    activatedIndex,
    switchIdx,
    isSearch,
    selectedSystem,
  ]);

  const setSuggestionsHandler = (suggestions, id) => {
    setMachineSuggestions(suggestions);
    setActivatedIndex(id);
  };

  const handleMakeSuggestions = useCallback(
    (system) => {
      if (system === 'ess') {
        setSuggestionsHandler(state.selectedEssMachines, 0);
      } else if (system === 'tgs') {
        setSuggestionsHandler(state.selectedTgsMachines, 1);
      } else if (system === 'tes') {
        setSuggestionsHandler(state.selectedTesMachines, 2);
      } else if (system === 'essDc') {
        setSuggestionsHandler(state.selectedEssDcMachines, 3);
      } else if (system === 'tgsDc') {
        setSuggestionsHandler(state.selectedTgsDcMachines, 4);
      } else if (system === 'tesDc') {
        setSuggestionsHandler(state.selectedTesDcMachines, 5);
      } else if (system === 'hpDc') {
        setSuggestionsHandler(state.selectedHpDcMachines, 6);
      } else if (system === 'hpEc') {
        setSuggestionsHandler(state.selectedHpElectricMachines, 7);
      } else if (system === 'hpGc') {
        setSuggestionsHandler(state.selectedHpGasMachines, 8);
      }
    },
    [state]
  );

  const handleSelectSuggestion = useCallback(
    (idx, suggestion, selectedSuggestion) => {
      const arr = [...inputSearch];
      arr[idx] = suggestion;

      if (selectedSuggestion) {
        const splitSuggestion = suggestion.split(' - ');
        const locationName = splitSuggestion[0];
        let specificLocationName;
        let machineName;

        let result;
        if (splitSuggestion.length > 2) {
          specificLocationName = splitSuggestion[1];
          machineName = splitSuggestion[2];
          result = ['locationName', 'specificLocationName', 'machineName']
            .reduce((acc, cur) => {
              return `${acc} - ${locations.all[locationName][specificLocationName]?.devices[machineName][cur]}`;
            }, [])
            .slice(2);
        } else {
          machineName = splitSuggestion[1];
          result = ['locationName', 'machineName']
            .reduce((acc, cur) => {
              return `${acc} - ${locations.all[locationName]?.devices?.[machineName]?.[cur]}`;
            }, [])
            .slice(2);
        }

        setSearchedSwtName(arr);
        setInputSearch([result]);
      } else {
        setInputSearch(arr);
      }

      if (suggestion?.length >= 2) {
        setIsExpand(filledCopy(isExpand, false));
        setSrc(filledCopy(src, closeArrow));
        setExpandButtonTitle('expand');
      }
    },
    [inputSearch, locations.all, src, isExpand]
  );

  const handleSelect = (suggestion) => {
    if (!suggestion) return;

    handleSelectSuggestion(0, suggestion, true);
    setInputSwitchName('');
    setSelectedSuggestionIdx(-1);
    setDisplaySuggestions(false);
  };

  const closeSuggestionsHandler = () => {
    setIsExpand(filledCopy(isExpand, false));
    setSrc(filledCopy(src, closeArrow));
    setExpandButtonTitle('expand');
  };

  const handleSubmitSearch = () => {
    const suggestion =
      filteredSuggestions?.[selectedSuggestionIdx] ?? filteredSuggestions?.[0];

    if (!suggestion) return;

    handleSelect(suggestion);
    closeSuggestionsHandler();
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'Escape': {
        setInputSearch(['']);
        break;
      }
      case 'Enter': {
        handleSubmitSearch();
        break;
      }
      case 'ArrowUp': {
        selectedSuggestionIdx > 0
          ? setSelectedSuggestionIdx(selectedSuggestionIdx - 1)
          : setSelectedSuggestionIdx(filteredSuggestions.length - 1);
        break;
      }
      case 'ArrowDown': {
        selectedSuggestionIdx < filteredSuggestions.length - 1
          ? setSelectedSuggestionIdx(selectedSuggestionIdx + 1)
          : setSelectedSuggestionIdx(0);
        break;
      }
      default: {
        break;
      }
    }
  };

  return (
    <ShadowWrapper className='telemetry-report'>
      <Wrapper>
        <FlexDiv>
          <Title
            image={
              checkIsSystemAndSwitchesSelected &&
              (systemSwitches[0][5] === 'ess' ||
              systemSwitches[0][5] === 'essDc'
                ? essDropPinLogo
                : systemSwitches[0][5] === 'tgs'
                ? tgsDropPinLogo
                : systemSwitches[0][5] === 'tes'
                ? tesDropPinLogo
                : systemSwitches[0][5] === 'tgsDc' ||
                  systemSwitches[0][5] === 'tesDc'
                ? tgsTesDropPinLogo
                : (systemSwitches[0][5] === 'hpDc' ||
                    systemSwitches[0][5] === 'hpEc' ||
                    systemSwitches[0][5] === 'hpGc') &&
                  '')
            }
            system={checkIsSystemAndSwitchesSelected && systemSwitches[0][0]}
            consumption={
              checkIsSystemAndSwitchesSelected &&
              (systemSwitches[0][5] === 'tgs' || systemSwitches[0][5] === 'hpGc'
                ? 'gas consumption vs time'
                : systemSwitches[0][5] === 'tes' ||
                  systemSwitches[0][5] === 'hpEc' ||
                  systemSwitches[0][5] === 'ess'
                ? 'energy consumption vs time'
                : 'data consumption vs time')
            }
            time={
              checkIsSystemAndSwitchesSelected &&
              `${displayStart} - ${displayEnd}`
            }
            numUnits={
              checkIsSystemAndSwitchesSelected &&
              telemetryData?.length + ' ' + 'units'
            }
          />
          <SearchBar
            handleClearSearch={handleClearSearch}
            handleExpandSearch={handleExpandSearch}
            buttonTitle={expandButtonTitle}
            systemSwitches={systemSwitches}
            handleSelectSuggestion={handleSelectSuggestion}
            setInputSwitchName={setInputSwitchName}
            setSelectedSuggestionIdx={setSelectedSuggestionIdx}
            handleMakeSuggestions={handleMakeSuggestions}
            handleKeyDown={handleKeyDown}
            handleSubmitSearch={handleSubmitSearch}
            switchIdx={switchIdx}
            displaySuggestions={displaySuggestions}
            activatedIndex={activatedIndex}
            selectedSuggestionIdx={selectedSuggestionIdx}
            filteredSuggestions={filteredSuggestions}
            setDisplaySuggestions={setDisplaySuggestions}
            inputSearch={inputSearch}
            disabled={!permissions[PERMISSIONS.SEARCH_TELEMETRY]}
          />
          <SwitchSpecification
            handleClearSearch={handleClearSearch}
            systemSwitches={systemSwitches}
            isExpand={isExpand}
            src={src}
            handleExpand={handleExpandEachLocation}
            system={system}
            inputSearch={inputSearch}
            setSelectedLocations={setSelectedLocations}
            selectedLocations={selectedLocations}
            specificLocationsOpeningControl={specificLocationsOpeningControl}
            setSpecificLocationsOpeningControl={
              setSpecificLocationsOpeningControl
            }
            totalDataConsumpOrEnergyConsump={totalDataConsumpOrEnergyConsump}
            setTotalDataConsumpOrEnergyConsump={
              setTotalDataConsumpOrEnergyConsump
            }
            totalUsageHours={totalUsageHours}
            setTotalUsageHours={setTotalUsageHours}
            totalNumOfSwt={totalNumOfSwt}
            setTotalNumOfSwt={setTotalNumOfSwt}
            swtName={selectedSystem?.split(' ')[0]}
            telemetryData={telemetryData}
            isDataChanged={isDataChanged}
            isDc={isDc}
            searchedSwtName={searchedSwtName}
            setSearchedSwtName={setSearchedSwtName}
            setIsExpand={setIsExpand}
            selectSpecific={selectSpecific}
          />
        </FlexDiv>
      </Wrapper>
    </ShadowWrapper>
  );
};

export default MainSwitchesSpecifications;

const ShadowWrapper = styled.div`
  width: 1202px;
  height: auto;
  padding-top: 2px;
  padding-bottom: 2px;

  ${layerBDark}

  border-radius: 8px;
  opacity: 1;
  ${flexBoxCenter}
`;

const Wrapper = styled.div`
  width: 1198px;
  height: auto;

  ${layerA90Deg}

  border-radius: 6px;
  opacity: 1;
  ${alignItemsFlexStart}
  flex-direction: column;
  gap: 4px;
`;

const FlexDiv = styled.div`
  width: 100%;
  height: auto;
  margin-left: 3px;
`;
